/**
 * TRD-00 §5 — the fixed timestep.
 *
 * The simulation never sees `dt` as a variable. The render loop accumulates real elapsed
 * time and steps the sim 0..N times per frame; every step advances exactly DT seconds.
 * Speed controls change how many steps are requested, never the size of a step, which is
 * why a run played at 4× hashes identically to the same run at 1× (TRD-00 §5.1).
 */
export const TICK_RATE = 60;
export const DT = 1 / TICK_RATE;
export const DT_MS = 1000 / TICK_RATE;

/** Clamp on a single frame's elapsed time, to avoid a death spiral after a tab stall. */
export const MAX_FRAME_MS = 250;

/** Upper bound on sim steps per frame at 1× speed; scaled by the speed multiplier. */
export const MAX_STEPS_PER_FRAME = 8;

/** UI snapshots are published at ~20 Hz, not every tick (TRD-00 §6). */
export const UI_PUBLISH_INTERVAL_MS = 50;

// ── Map + spatial index (TRD-05 §2) ───────────────────────────────────────────────────

export const MAP_WIDTH = 32;
export const MAP_HEIGHT = 18;

/** 2×2-tile cells, giving a 16×9 grid over the map (TRD-05 R-05-01). */
export const GRID_CELL_TILES = 2;
export const GRID_COLS = MAP_WIDTH / GRID_CELL_TILES;
export const GRID_ROWS = MAP_HEIGHT / GRID_CELL_TILES;

// ── Combat (TRD-05) ───────────────────────────────────────────────────────────────────

/** R-05-15. Three seconds; a projectile alive past this is a bug, not a shot. */
export const PROJECTILE_LIFETIME_TICKS = 180;

/**
 * R-05-14. Above this speed a projectile can step clean over a small enemy in one tick,
 * so collision switches from a point test to a swept test against the segment travelled.
 */
export const SWEPT_COLLISION_TILES_PER_TICK = 0.5;

/** R-05-16 defaults. Overridden per-tower by synergies, never read from content directly. */
export const CHAIN_RANGE_TILES = 3;
export const CHAIN_FALLOFF = 0.5;

/**
 * R-05-31. A missile accelerates from its launch speed, but not forever — the 3 s
 * lifetime is far too generous a bound (30 tiles/s² would reach 90 tiles/s, four times
 * the width of the map per second, and tunnel through everything). Capping at a multiple
 * of the launch speed keeps the ramp readable and the swept collision test honest.
 */
export const MISSILE_MAX_SPEED_FACTOR = 3;

/**
 * R-05-32. How long the renderer holds a sustained laser line after the damage tick that
 * emitted it. Two ticks of slack past the cadence so the line never blinks between ticks
 * at low frame rates.
 */
export const LASER_HOLD_SLACK_TICKS = 2;

/** R-05-28. A salvo wider than this stops reading as one shot and starts reading as spray. */
export const MAX_SALVO_SPREAD_RADIANS = 1.6;

// ── Statuses (TRD-05 §7) ──────────────────────────────────────────────────────────────

/** R-05-22. Venom-4 raises this to 10; the base cap lives here. */
export const POISON_MAX_STACKS = 5;
export const POISON_MAX_STACKS_VENOM4 = 10;

/** R-05-23. Every 30 ticks is 0.5 s; Venom-4 halves the interval, not the damage. */
export const POISON_TICK_INTERVAL = 30;
export const POISON_TICK_INTERVAL_VENOM4 = 15;

/** R-05-24. Poison damage is `stacks * 2 * DAMAGE_PER_STACK`, dealt as `true`. */
export const POISON_DAMAGE_PER_STACK = 1;

/** R-05-22. Three shock stacks consume into a stun. */
export const SHOCK_MAX_STACKS = 3;
export const SHOCK_STUN_TICKS = 45;

/**
 * Ticks an enemy shrugs off further stuns/freezes for, measured from when the last one
 * ENDS (playtest report: "tempest can perma stun enemies").
 *
 * Tempest-6 applies a shock stack on every hit and three stacks convert to a stun. A
 * chaining Tempest board lands far more than three hits per stun, so the next stun was
 * always ready before the current one expired — an enemy that entered the board's range
 * never moved again. Nothing in the pipeline said "no", because every individual
 * application was legal.
 *
 * Hard CC is capped by RECOVERY rather than by nerfing the proc, because the proc is what
 * the class is for. At 45 ticks of stun and 120 of recovery a locked-down enemy still
 * moves for two thirds of the time, and Frost's field freeze and Tempest's shock now
 * compete for the same window instead of stacking into a permanent hold.
 */
export const HARD_CC_RECOVERY_TICKS = 120;

/**
 * The support surge (playtest request): support towers idle at HALF their old aura and
 * can be fired once a wave to pay out at DOUBLE it.
 *
 * Four, because the surge is measured against the passive it replaces: half of the old
 * value surging to twice the old value is a factor of four between them. It multiplies
 * the BONUS, never the multiplier — 1.05 becomes 1.20, not 4.2.
 */
export const SUPPORT_SURGE_FACTOR = 4;
/**
 * A surge lasts the whole ROUND and then sits out this many (playtest request).
 *
 * Measured in waves rather than seconds because that is the unit the decision is made in:
 * "is this the round I need it" is a question a player can answer, where "are the next
 * five seconds the right five seconds" is not one anyone can see coming. It also makes
 * the choice a plan — three rounds out means committing to which of the next three
 * matters most.
 */
export const SUPPORT_SURGE_COOLDOWN_WAVES = 3;

/**
 * After a disable ends, the tower cannot be disabled again for this long.
 *
 * The same guard `HARD_CC_RECOVERY_TICKS` gives enemies, for the same reason and learned
 * the same way: Tempest could once perma-stun, and two Disablers on a 10s-on cycle can
 * hold a board down forever without this. The failure mode is not "hard" — it is "my
 * towers stopped working and never started again", which reads as a broken game.
 */
export const TOWER_DISABLE_RECOVERY_TICKS = 180;

/** R-05-18 vulnerability multipliers, applied at damage stage 5. */
export const CHILL_VULNERABILITY = 1.15;
export const MARK_VULNERABILITY = 1.2;

/**
 * TRD-10 R-10-17 — bump on ANY change to sim behaviour: system order, RNG usage,
 * damage rounding, balance numbers, the daily generator, or the solvability probe.
 * The server rejects submissions whose simVersion does not match its own.
 */
/**
 * The global wave health curve (R-06-10): what wave N multiplies every enemy's hp by.
 *
 * Two rates, because one was not enough. It was `1 + 0.11 * (wave - 1)` — LINEAR, reaching
 * 3.6x by the finale while the threat budget that buys enemy COUNTS compounds to about 71x
 * over the same span, so a late wave was an enormous crowd of very nearly wave-2 enemies.
 * That is why splash damage ran away with the damage charts, and why adding more numbers
 * could never create pressure.
 *
 * A single geometric rate replaced it and was still too soft: the owner played it and the
 * verdict was unchanged. The reason is that a board does not grow geometrically either. It
 * grows very fast to about wave 7 and then plateaus, so a constant rate that is gentle
 * enough to leave the opening alone is far too gentle by wave 20. Hence the second rate,
 * mirroring `lateThreatGrowth` on the counts side.
 *
 * 1.10, then an extra 1.07 per wave from `ENEMY_HP_LATE_FROM_WAVE`:
 *
 *     wave  6     10    15     20     25     28
 *     old   1.55   2.00   2.54   3.09   3.64   3.97   (linear)
 *     now   1.61   2.65   4.95   9.25  17.27  25.13
 *
 * The second rate starts at wave 7 for a specific reason: the daily generator's solvability
 * gate asks its probe to reach wave 6 and NOTHING further, so wave 6 is the last wave that
 * decides whether a day generates at all. It is also exactly where the measured headroom
 * peaks — a strong board is roughly three times more comfortable at wave 6 than at wave 1,
 * because it grows about tenfold over the opening and then plateaus. So wave 7 is both the
 * first wave that is free to bite and the first wave that should.
 *
 * Compounded by repeated multiplication rather than `Math.pow` — the determinism contract
 * forbids the operator, and a last-bit difference in a health total is a hash mismatch.
 */
export const ENEMY_HP_GROWTH = 1.1;
export const ENEMY_HP_LATE_GROWTH = 1.03;
export const ENEMY_HP_LATE_FROM_WAVE = 7;

export const SIM_VERSION = 31;
